/**
 * Tabulator stĺpce pre CRM Komunikácia zoznamy (Moja agenda / Úplný zoznam / FP).
 * minWidth: mntn001; frozen semafory; Vykonanie+deadline ako dateUserHeaderFilter (parita FP).
 */
import React from 'react';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { reactFormatter } from 'react-tabulator';
import StatusIndicator, { getStatusIndicatorFilterValue } from '../components/StatusIndicator';
import { dateUserHeaderFilter } from './dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from './inputOnEnterHeaderFilter';
import { primeMultiSelectHeaderFilter } from './primeMultiSelectHeaderFilter';
import { TABULATOR_LIST_ID_MIN_WIDTH, tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth } from './tabulatorColumnMinWidth';
import {
  buildCrmWorkflowStatusFilterOptions,
  getEffectiveCrmTicketStatusId,
} from './crmCommunicationTicketStatus';
import { getEffectiveMask } from './effectiveMask';
import { crmSetupStateDisplayHex, displayCrmStepName } from './crmStateI18n';
import { DeleteIcon, EditRowIcon, SeenIcon, UnseenIcon } from '../pages/svg/svgIcons';
import { crmCommunicationTypeLabelKey, renderCrmCommunicationTypeIcon, CRM_COMMUNICATION_TYPE_FILTER_VALUES } from './crmCommunicationTypeIcons';
import { CrmTicketListDeadlineCell, CrmTicketListStatusCell } from '../components/crm/communication/CrmTicketListCells';
import { resolveWorkflowNextStepListLabel } from './workflowNextStepLabel';
import { parseCrmHeaderMulti } from './crmCommunicationTicketRequestParams';
import { defaultProductLogoSrc } from '../constants/brandingDisplay';
import { buildCanonicalPrintFooterHtml, buildCanonicalPrintHeaderHtml } from './printChrome';

/** User/standard mask page keys – cust019 / cust021 (CRM_KOMUNIKACIA_SPEC §5.1, §5.3). */
export const CRM_COMM_PAGE_MY_AGENDA = 'CrmCommunicationTicketsList_MyAgenda';
export const CRM_COMM_PAGE_COMPLETE_LIST = 'CrmCommunicationTicketsList_CompleteList';
export const CRM_COMM_PAGE_CUSTOMER_DETAIL_COMMUNICATION = 'CustomerMyCustomersDetail_Communication';
export const CRM_COMM_PAGE_CUSTOMER_DETAIL_QUOTES = 'CustomerMyCustomersDetail_Quotes';
export const CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_COMMUNICATION = 'CustomerAllPartnersDetail_Communication';
export const CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_QUOTES = 'CustomerAllPartnersDetail_Quotes';
export const CRM_COMM_PAGE_MY_BUSINESS_BY_STATE = 'CustomerMyBusinessByStateList';
export const CRM_COMM_PAGE_ALL_BUSINESS_BY_STATE = 'CustomerAllBusinessByStateList';
export const CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES = 'CustomerMyBusinessByCustomerDetail_Quotes';
export const CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_COMMUNICATION = 'CustomerMyBusinessByCustomerDetail_Communication';
export const CRM_COMM_PAGE_ALL_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES = 'CustomerAllBusinessByCustomerDetail_Quotes';
export const CRM_COMM_PAGE_ALL_BUSINESS_BY_CUSTOMER_DETAIL_COMMUNICATION = 'CustomerAllBusinessByCustomerDetail_Communication';
export const CRM_COMM_PAGE_MY_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS = 'CustomerMyAcquisitionByCustomerDetail_Acquisitions';
export const CRM_COMM_PAGE_ALL_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS = 'CustomerAllAcquisitionByCustomerDetail_Acquisitions';
export const CRM_COMM_PAGE_MY_ACQUISITION_BY_STATE = 'CustomerMyAcquisitionByStateList';
export const CRM_COMM_PAGE_ALL_ACQUISITION_BY_STATE = 'CustomerAllAcquisitionByStateList';

const CRM_COMM_AGENDA_LIST_PAGES = new Set([CRM_COMM_PAGE_MY_AGENDA, CRM_COMM_PAGE_COMPLETE_LIST]);

/** Zdieľanie stĺpcovej masky cust019 aj pre embedded karty v detaile zákazníka (cust014). */
const CRM_COMM_TICKETS_COLUMN_MASK_ALIASES = new Set([
  ...CRM_COMM_AGENDA_LIST_PAGES,
  CRM_COMM_PAGE_CUSTOMER_DETAIL_COMMUNICATION,
  CRM_COMM_PAGE_CUSTOMER_DETAIL_QUOTES,
  CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_COMMUNICATION,
  CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_QUOTES,
  CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES,
  CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_COMMUNICATION,
  CRM_COMM_PAGE_ALL_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES,
  CRM_COMM_PAGE_ALL_BUSINESS_BY_CUSTOMER_DETAIL_COMMUNICATION,
  CRM_COMM_PAGE_MY_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS,
  CRM_COMM_PAGE_ALL_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS,
  CRM_COMM_PAGE_MY_ACQUISITION_BY_STATE,
  CRM_COMM_PAGE_ALL_ACQUISITION_BY_STATE,
]);

/** @typedef {'crm_full' | 'portal' | 'crm_tickets'} CrmCommColumnLayoutMode */

/** Indikátor termínu – frozen vľavo (parita ServiceTaskList; stav ACQ/QTE je v stĺpci partnera). */
export const CRM_COMM_FROZEN_FIELDS = Object.freeze(['status_indicator']);

/** Frozen stĺpce pre cust019/cust021 – editor + indikátor termínu. */
export const CRM_COMM_TICKETS_FROZEN_FIELDS = Object.freeze(['editor', 'status_indicator']);

/** Frozen stĺpce pre cust027 (Podľa stavu) – výber + editor + semafor. */
export const CRM_COMM_STATE_LIST_FROZEN_FIELDS = Object.freeze(['rowSelection', 'editor', 'status_indicator']);

function normalizeSemaphoreColorToken(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'green' || s === '#16a34a' || s === '#22c55e') return 'green';
  if (s === 'orange' || s === '#f59e0b' || s === '#fb923c') return 'orange';
  if (s === 'red' || s === '#dc2626' || s === '#ef4444') return 'red';
  return s;
}

function crmStateHexColor(raw) {
  const token = normalizeSemaphoreColorToken(raw);
  if (!token) return '#9ca3af';
  if (token === 'green') return '#16a34a';
  if (token === 'orange') return '#f59e0b';
  if (token === 'red') return '#dc2626';
  if (token.startsWith('#')) return token;
  return crmSetupStateDisplayHex(raw);
}

/**
 * Poradie stĺpcov – len embedded CRM zoznamy bez crmTicketsLayout (nie cust019/cust021).
 * Moja agenda a Úplný zoznam používajú CRM_COMM_TICKETS_COLUMN_ORDER (§5.1 = parita FP).
 */
export const CRM_COMM_COLUMN_ORDER = Object.freeze([
  'status_indicator',
  'type',
  'is_unread',
  'ticket_code',
  'title',
  'primary_company_name',
  'execution_crm_step_id',
  'status_id',
  'waiting_for_action_by',
  'primary_contact_name',
  'created_at',
  'pricing_speed_days',
  'quote_offer_by_name',
  'quote_price',
  'quote_offer_status',
  'decision_duration_days',
  'last_activity_at',
  'rejection_reason',
]);

/**
 * Poradie stĺpcov – Firemný portál (§5.2): deadline semafor, potom stav ACQ/QTE pred partnerom.
 */
export const CRM_COMM_PORTAL_COLUMN_ORDER = Object.freeze([
  'status_indicator',
  'type',
  'is_unread',
  'ticket_code',
  'title',
  'primary_company_name',
  'execution_crm_step_id',
  'status_id',
  'waiting_for_action_by',
  'created_at',
  'last_activity_at',
]);

export const DEFAULT_CRM_COMM_COLUMNS_VISIBLE = Object.freeze({
  status_indicator: true,
  type: true,
  is_unread: true,
  ticket_code: true,
  title: true,
  primary_company_name: true,
  execution_crm_step_id: true,
  primary_contact_name: true,
  created_at: true,
  status_id: true,
  waiting_for_action_by: true,
  pricing_speed_days: true,
  quote_offer_by_name: true,
  quote_price: true,
  quote_offer_status: true,
  decision_duration_days: true,
  last_activity_at: true,
  rejection_reason: true,
});

/**
 * Poradie stĺpcov – cust019 Moja agenda + cust021 Úplný zoznam (§5.1, §5.3 – rovnaká maska, parita FP).
 */
export const CRM_COMM_TICKETS_COLUMN_ORDER = Object.freeze([
  'editor',
  'status_indicator',
  'id',
  'type',
  'is_unread',
  'ticket_code',
  'title',
  'primary_company_name',
  'trader_name',
  'execution_crm_step_id',
  'status_id',
  'waiting_for_action_by',
  'created_at',
  'last_activity_at',
  'primary_contact_name',
  'pricing_speed_days',
  'quote_offer_by_name',
  'quote_price',
  'quote_offer_status',
  'decision_duration_days',
  'rejection_reason',
  'custom_delete',
]);

/** Poradie stĺpcov – cust027/cust030 Môj/Všetky obchod → Podľa stavu (toolbar edit/zmazať, bez custom_delete). */
export const CRM_COMM_STATE_LIST_COLUMN_ORDER = Object.freeze([
  'rowSelection',
  'editor',
  'status_indicator',
  ...CRM_COMM_TICKETS_COLUMN_ORDER.filter((f) => f !== 'editor' && f !== 'status_indicator' && f !== 'type' && f !== 'custom_delete'),
]);

export const DEFAULT_CRM_COMM_TICKETS_COLUMNS_VISIBLE = Object.freeze({
  editor: true,
  status_indicator: true,
  id: true,
  type: true,
  is_unread: true,
  ticket_code: true,
  title: true,
  primary_company_name: true,
  trader_name: true,
  execution_crm_step_id: true,
  status_id: true,
  waiting_for_action_by: true,
  created_at: true,
  last_activity_at: true,
  primary_contact_name: false,
  pricing_speed_days: false,
  quote_offer_by_name: false,
  quote_price: false,
  quote_offer_status: false,
  decision_duration_days: false,
  rejection_reason: false,
  custom_delete: false,
});

export const CRM_COMM_TICKETS_PRINT_COLUMNS_ALLOWED = Object.freeze([
  'status_indicator',
  'id',
  'type',
  'is_unread',
  'ticket_code',
  'title',
  'primary_company_name',
  'trader_name',
  'execution_crm_step_id',
  'status_id',
  'waiting_for_action_by',
  'created_at',
  'last_activity_at',
]);

/** Popisky pre mask dialog keď je v hlavičke tabuľky prázdny titulok (semafory). */
export const CRM_COMM_MASK_FIELD_LABEL_KEYS = Object.freeze({
  status_indicator: 'CrmCommunication.List.SemaphoreDeadline',
});

/** Full CRM / Moja agenda column title keys (non-tickets layout). */
export const CRM_COMM_FULL_COLUMN_I18N_KEYS = Object.freeze({
  status_indicator: 'CrmCommunication.List.SemaphoreDeadline',
  id: 'ServiceTaskTable.Col.Id',
  type: 'CrmCommunication.List.Type',
  is_unread: 'CrmCommunication.List.Read',
  ticket_code: 'CrmCommunication.List.Code',
  title: 'CrmCommunication.List.TitleCol',
  primary_company_name: 'CrmCommunication.List.Customer',
  trader_name: 'CrmCommunication.List.Trader',
  execution_crm_step_id: 'CrmCommunication.List.NextStep',
  primary_contact_name: 'CrmCommunication.List.Buyer',
  status_id: 'CrmCommunication.List.InquiryState',
  waiting_for_action_by: 'CrmCommunication.List.WaitingForActionBy',
  pricing_speed_days: 'CrmCommunication.List.PricingSpeed',
  quote_offer_by_name: 'CrmCommunication.List.OfferByAt',
  quote_price: 'CrmCommunication.List.QuotePrice',
  quote_offer_status: 'CrmCommunication.List.OfferStatus',
  decision_duration_days: 'CrmCommunication.List.DecisionDuration',
  created_at: 'CrmCommunication.List.FoundedBy',
  last_activity_at: 'CrmCommunication.List.LastActivity',
  rejection_reason: 'CrmCommunication.List.Rejection',
  custom_delete: 'ServiceTaskTable.Col.Delete',
});

export const CRM_COMM_TICKETS_MASK_FIELD_LABEL_KEYS = Object.freeze({
  status_indicator: 'CrmCommunication.List.SemaphoreDeadline',
  id: 'ServiceTaskTable.Col.Id',
  type: 'ServiceTaskTable.Col.Type',
  is_unread: 'ServiceTaskTable.Col.ReadState',
  ticket_code: 'ServiceTaskTable.Col.TicketNumber',
  title: 'ServiceTaskTable.Col.Name',
  primary_company_name: 'CrmCommunication.List.PartnerName',
  trader_name: 'CrmCommunication.List.Trader',
  execution_crm_step_id: 'CrmCommunication.List.NextStep',
  primary_contact_name: 'CrmCommunication.List.Buyer',
  status_id: 'ServiceTaskTable.Col.Status',
  waiting_for_action_by: 'ServiceTaskTable.Col.Deadline',
  pricing_speed_days: 'CrmCommunication.List.PricingSpeed',
  quote_offer_by_name: 'CrmCommunication.List.OfferByAt',
  quote_price: 'CrmCommunication.List.QuotePrice',
  quote_offer_status: 'CrmCommunication.List.OfferStatus',
  decision_duration_days: 'CrmCommunication.List.DecisionDuration',
  created_at: 'ServiceTaskTable.Col.Created',
  last_activity_at: 'ServiceTaskTable.Col.Updated',
  rejection_reason: 'CrmCommunication.List.Rejection',
  custom_delete: 'ServiceTaskTable.Col.Delete',
});

/**
 * Základné možnosti ReactTabulator pre CRM komunikáciu (cust019/cust021 aj FP).
 * Samostatná kópia – bez importu z modulu firemného portálu.
 */
export function buildCrmCommunicationTabulatorOptions({
  t,
  embedded,
  embeddedFillParent = false,
  stateListLayout = false,
  listTitle,
  pageSizePc,
  ajaxRequestFunc,
  toast,
  onAjaxRequesting,
  onAjaxResponse,
  onAjaxError,
  getTable,
  branding = null,
  companySettingsPrint = null,
  moduleSettings = null,
}) {
  const listHeight = stateListLayout ? 'calc(100vh - 193px)' : 'calc(100vh - 313px)';
  return {
    index: 'id',
    layout: 'fitColumns',
    renderHorizontal: 'virtual',
    rowHeight: 42,
    dataLoader: true,
    dataLoaderLoading: t('Common.LoadingData'),
    dataLoaderError: t('Common.LoadFailed'),
    placeholder: t('Common.NoData'),
    height: embeddedFillParent ? '100%' : (embedded ? undefined : listHeight),
    progressiveLoad: 'scroll',
    progressiveLoadScrollMargin: 80,
    progressiveLoadDelay: 200,
    paginationSize: pageSizePc,
    pagination: false,
    filterMode: 'remote',
    sortMode: 'remote',
    ajaxURL: '/api/crm_communication_tickets/paged',
    ajaxParams: () => ({}),
    ajaxRequestFunc,
    ajaxRequesting: () => {
      if (typeof onAjaxRequesting === 'function') {
        const proceed = onAjaxRequesting();
        if (proceed === false) return false;
      }
      const table = typeof getTable === 'function' ? getTable() : null;
      if (table?.initialized) table.alert?.(t('Common.LoadingData'));
      return true;
    },
    ajaxResponse: onAjaxResponse,
    ajaxError: onAjaxError,
    movableColumns: true,
    headerSortTristate: true,
    autoResize: true,
    scrollToColumnIfVisible: false,
    printAsHtml: true,
    printHeader: () => buildCanonicalPrintHeaderHtml({
      branding,
      productFallbackSrc: defaultProductLogoSrc,
      companySettingsPrint,
      moduleSettings,
      title: listTitle,
      printDateLabel: t('Print.DateLabel'),
    }),
    printFooter: () => buildCanonicalPrintFooterHtml({ companySettingsPrint }),
    printRowRange: 'active',
  };
}

/**
 * @param {CrmCommColumnLayoutMode} layoutMode
 */
export function getDefaultCrmCommColumnsVisible(layoutMode = 'crm_full', stateListLayout = false) {
  if (layoutMode === 'portal') {
    return {
      status_indicator: true,
      type: true,
      is_unread: true,
      ticket_code: true,
      title: true,
      primary_company_name: true,
      execution_crm_step_id: true,
      status_id: true,
      waiting_for_action_by: true,
      created_at: true,
      last_activity_at: true,
    };
  }
  if (layoutMode === 'crm_tickets') {
    const defaults = { ...DEFAULT_CRM_COMM_TICKETS_COLUMNS_VISIBLE };
    if (stateListLayout) delete defaults.type;
    return defaults;
  }
  return { ...DEFAULT_CRM_COMM_COLUMNS_VISIBLE };
}

/**
 * @param {object} [saved]
 * @param {CrmCommColumnLayoutMode} [layoutMode]
 */
/**
 * cust027/cust030 zdieľajú stĺpcovú masku (cust030 fallback na cust027).
 */
const CRM_COMM_STATE_LIST_COLUMN_MASK_ALIASES = Object.freeze({
  [CRM_COMM_PAGE_ALL_BUSINESS_BY_STATE]: CRM_COMM_PAGE_MY_BUSINESS_BY_STATE,
  [CRM_COMM_PAGE_ALL_ACQUISITION_BY_STATE]: CRM_COMM_PAGE_MY_ACQUISITION_BY_STATE,
});

/**
 * cust019/cust021 zdieľajú uloženú masku stĺpcov (§5.3) – filtre ostávajú na vlastnom page key.
 */
export function crmCommColumnMaskPage(listPageKey, crmTicketsLayout = false) {
  if (crmTicketsLayout && CRM_COMM_STATE_LIST_COLUMN_MASK_ALIASES[listPageKey]) {
    return CRM_COMM_STATE_LIST_COLUMN_MASK_ALIASES[listPageKey];
  }
  if (crmTicketsLayout && CRM_COMM_TICKETS_COLUMN_MASK_ALIASES.has(listPageKey)) {
    return CRM_COMM_PAGE_MY_AGENDA;
  }
  return listPageKey;
}

/**
 * @param {string} listPageKey
 * @param {object} state
 * @param {boolean} [crmTicketsLayout]
 */
export function getCrmCommEffectiveMask(listPageKey, state, crmTicketsLayout = false) {
  const columnPage = crmCommColumnMaskPage(listPageKey, crmTicketsLayout);
  const pageMask = getEffectiveMask(listPageKey, state);
  if (columnPage === listPageKey) {
    return pageMask;
  }
  const columnMask = getEffectiveMask(columnPage, state);
  if (!columnMask) {
    return pageMask;
  }
  const pickColumns = (maskRow) => {
    if (!maskRow || typeof maskRow !== 'object') return null;
    const cols = maskRow.columns;
    return cols != null ? cols : null;
  };
  const pickAttributes = (maskRow) => {
    if (!maskRow || typeof maskRow !== 'object') return null;
    const attrs = maskRow.attributes;
    return attrs != null ? attrs : null;
  };
  let sharedColumns = null;
  let sharedAttributes = null;
  if (columnMask.type === 'user' && columnMask.userMask) {
    sharedColumns = pickColumns(columnMask.userMask);
    sharedAttributes = pickAttributes(columnMask.userMask);
  } else if (columnMask.type === 'standard' && columnMask.standardMask) {
    sharedColumns = pickColumns(columnMask.standardMask);
    sharedAttributes = pickAttributes(columnMask.standardMask);
  }
  if (pageMask?.type === 'user' && pageMask.userMask) {
    return {
      type: 'user',
      userMask: {
        ...pageMask.userMask,
        ...(sharedColumns != null ? { columns: sharedColumns } : {}),
        ...(sharedAttributes != null ? { attributes: sharedAttributes } : {}),
      },
    };
  }
  if (sharedColumns != null || sharedAttributes != null) {
    return {
      type: 'user',
      userMask: {
        columns: sharedColumns ?? {},
        attributes: sharedAttributes ?? [],
        filters: pageMask?.userMask?.filters ?? {},
      },
    };
  }
  return columnMask;
}

export function mergeCrmCommColumnVisibility(saved, layoutMode = 'crm_full', stateListLayout = false) {
  const defaults = getDefaultCrmCommColumnsVisible(layoutMode, stateListLayout);
  const merged = { ...defaults };
  if (saved && typeof saved === 'object') {
    Object.keys(defaults).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
    if (
      Object.prototype.hasOwnProperty.call(saved, 'workflow_state_label')
      && !Object.prototype.hasOwnProperty.call(saved, 'status_id')
      && Object.prototype.hasOwnProperty.call(merged, 'status_id')
    ) {
      merged.status_id = saved.workflow_state_label !== false;
    }
    if (
      Object.prototype.hasOwnProperty.call(saved, 'semaphore_a_color')
      && !Object.prototype.hasOwnProperty.call(saved, 'status_indicator')
      && Object.prototype.hasOwnProperty.call(merged, 'status_indicator')
    ) {
      merged.status_indicator = saved.semaphore_a_color !== false;
    }
  }
  CRM_COMM_FROZEN_FIELDS.forEach((f) => {
    if (Object.prototype.hasOwnProperty.call(merged, f)) merged[f] = true;
  });
  if (layoutMode === 'crm_tickets') {
    if (stateListLayout) {
      merged.custom_delete = false;
      CRM_COMM_STATE_LIST_FROZEN_FIELDS.forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(merged, f)) merged[f] = true;
      });
    } else {
      CRM_COMM_TICKETS_FROZEN_FIELDS.forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(merged, f)) merged[f] = true;
      });
    }
    if (Object.prototype.hasOwnProperty.call(merged, 'id')) merged.id = true;
  }
  if (!Object.values(merged).some(Boolean)) return { ...defaults };
  return merged;
}

/**
 * Remote paged: pri `range:` nechá filtrovanie na BE; inak lokálny match mena/dátumu (parita mntn001).
 */
export function createCrmCommDateColumnFilter(dateFormat, dateTimeFormat) {
  const dateFmt = dateFormat ?? 'dd.MM.yyyy';
  const dateTimeFmt = dateTimeFormat ?? `${dateFmt} HH:mm`;
  return function crmCommDateColumnFilter(headerValue, _rowValue, rowData, filterParams) {
    if (!headerValue || String(headerValue).trim() === '') return true;
    if (String(headerValue).startsWith('range:')) return true;
    const normalize = (str) =>
      (str || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const search = normalize(headerValue);
    const name = filterParams?.name;
    const dateVariants = (iso) => {
      const dt = DateTime.fromISO(String(iso || ''));
      if (!dt.isValid) return '';
      return [
        dt.toFormat(dateFmt),
        dt.toFormat(dateTimeFmt),
        dt.toFormat('HH:mm'),
        dt.toFormat('yyyy-MM-dd'),
      ].join(' ');
    };
    if (name === 'created_at') {
      const userName = rowData?.created_by_name ? String(rowData.created_by_name) : '';
      if (userName && normalize(userName).includes(search)) return true;
      return normalize(dateVariants(rowData?.created_at)).includes(search);
    }
    if (name === 'last_activity_at') {
      const userName = rowData?.last_activity_user_name ? String(rowData.last_activity_user_name) : '';
      if (userName && normalize(userName).includes(search)) return true;
      return normalize(dateVariants(rowData?.last_activity_at)).includes(search);
    }
    if (name === 'waiting_for_action_by') {
      const dl = rowData?.deadline ?? rowData?.waiting_for_action_deadline_at;
      if (dl && normalize(dateVariants(dl)).includes(search)) return true;
      const users = rowData?.awaiting_users ?? rowData?.awaiting_user ?? [];
      for (const u of users) {
        const uname = u?.employee?.name || u?.username;
        if (normalize(uname).includes(search)) return true;
      }
      const label = rowData?.waiting_for_action_by ? String(rowData.waiting_for_action_by) : '';
      if (label && normalize(label).includes(search)) return true;
    }
    return false;
  };
}

/** Parita FP `dateOrUserFilter` – deadline range + mená v `awaiting_users`. */
export function createCrmCommDateOrUserFilter(dateFormat, dateTimeFormat) {
  const dateFmt = dateFormat ?? 'dd.MM.yyyy';
  const dateTimeFmt = dateTimeFormat ?? `${dateFmt} HH:mm`;
  return function crmCommDateOrUserFilter(headerValue, _rowValue, rowData, filterParams) {
    if (!headerValue || String(headerValue).trim() === '') return true;
    const raw = String(headerValue).trim();
    if (raw.startsWith('range:')) {
      const parts = raw.slice('range:'.length).split(':');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        const from = DateTime.fromFormat(parts[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(parts[1], 'yyyy-MM-dd').endOf('day');
        if (!from.isValid || !to.isValid) return true;
        const field = filterParams?.name;
        const source = field === 'waiting_for_action_by'
          ? (rowData?.deadline ?? rowData?.waiting_for_action_deadline_at)
          : rowData?.[field];
        const dt = DateTime.fromISO(source || '');
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const normalize = (str) =>
      (str || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const search = normalize(headerValue);
    const dateVariants = (iso) => {
      const dt = DateTime.fromISO(String(iso || ''));
      if (!dt.isValid) return '';
      return [
        dt.toFormat(dateFmt),
        dt.toFormat(dateTimeFmt),
        dt.toFormat('HH:mm'),
        dt.toFormat('yyyy-MM-dd'),
      ].join(' ');
    };
    if (filterParams?.name === 'created_at') {
      const userName = rowData?.created_by_name ? String(rowData.created_by_name) : '';
      if (userName && normalize(userName).includes(search)) return true;
      return normalize(dateVariants(rowData?.created_at)).includes(search);
    }
    if (filterParams?.name === 'last_activity_at') {
      const userName = rowData?.last_activity_user_name ? String(rowData.last_activity_user_name) : '';
      if (userName && normalize(userName).includes(search)) return true;
      return normalize(dateVariants(rowData?.last_activity_at)).includes(search);
    }
    if (filterParams?.name === 'waiting_for_action_by') {
      const dl = rowData?.deadline ?? rowData?.waiting_for_action_deadline_at;
      if (dl && normalize(dateVariants(dl)).includes(search)) return true;
      const users = rowData?.awaiting_users ?? rowData?.awaiting_user ?? [];
      for (const u of users) {
        const uname = u?.employee?.name || u?.username;
        if (normalize(uname).includes(search)) return true;
      }
      const label = rowData?.waiting_for_action_by ? String(rowData.waiting_for_action_by) : '';
      if (label && normalize(label).includes(search)) return true;
    }
    return false;
  };
}

function ReadCell(props) {
  const { t } = props;
  const unread = !!props.cell.getValue();
  return <span>{unread ? t('CrmCommunication.List.Unread') : t('CrmCommunication.List.ReadOk')}</span>;
}

function MailIconCell(props) {
  const { t, onToggleRead } = props;
  const rowData = props.cell.getRow().getData();
  const isUnread = !!rowData?.is_unread;
  if (typeof onToggleRead === 'function') {
    return (
      <div className="flex items-center justify-center w-full h-full gap-2">
        <button
          type="button"
          title={isUnread ? t('ServiceTask.MarkAsRead') : t('ServiceTask.MarkAsUnread')}
          onClick={(e) => {
            e.stopPropagation();
            onToggleRead(rowData.id, !isUnread);
          }}
          className="hover:opacity-75"
        >
          {isUnread ? <UnseenIcon /> : <SeenIcon />}
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center w-full h-full gap-2">
      {isUnread ? <UnseenIcon /> : <SeenIcon />}
    </div>
  );
}

function mapCrmRowForStatusIndicator(rowData) {
  return {
    deadline: rowData?.deadline ?? rowData?.waiting_for_action_deadline_at ?? null,
    cancelled_at: rowData?.cancelled_at ?? null,
  };
}

const CRM_STATUS_INDICATOR_OPTS = Object.freeze({
  settingsModule: 'crm',
  deadlineField: 'waiting_for_action_deadline_at',
});

function CrmStatusIndicatorCell({ cell, moduleSettings, t, locale }) {
  const rowData = cell.getRow().getData();
  return (
    <div className="flex items-center justify-center w-full h-full">
      <StatusIndicator
        task={mapCrmRowForStatusIndicator(rowData)}
        moduleSettings={moduleSettings}
        t={t}
        locale={locale}
        settingsModule="crm"
        deadlineField="waiting_for_action_deadline_at"
      />
    </div>
  );
}

function EditRowCell({ cell, onOpenDetail }) {
  const rowData = cell.getRow().getData();
  const handleView = () => {
    if (typeof onOpenDetail === 'function') {
      onOpenDetail(rowData.id);
    }
  };
  return (
    <div className="flex justify-center items-start w-full h-full">
      <button type="button" onClick={handleView}>
        <EditRowIcon />
      </button>
    </div>
  );
}

function DeleteRowCell({ cell, onDelete }) {
  const rowData = cell.getRow().getData();
  return (
    <div className="flex items-center justify-center w-full h-full">
      <button
        type="button"
        className="w-fit flex flex-row px-1 py-1 border rounded-md bg-red-900"
        onClick={(e) => {
          e.stopPropagation();
          if (typeof onDelete === 'function') onDelete(rowData.id);
        }}
      >
        <DeleteIcon />
      </button>
    </div>
  );
}

function TruncateSpan({ text }) {
  const value = (text ?? '').toString();
  return <span className="block w-full min-w-0 truncate" title={value || undefined}>{value}</span>;
}

function CrmTicketStatusFormatter({ cell, crmStates, locale }) {
  const rowData = cell.getRow().getData();
  return <CrmTicketListStatusCell rowData={rowData} crmStates={crmStates} locale={locale} />;
}

function crmTicketStatusHeaderFilterFunc(headerValue, _rowValue, rowData, crmStates) {
  if (!headerValue) return true;
  const selected = new Set(parseCrmHeaderMulti(headerValue).map((x) => Number(x)).filter((n) => !Number.isNaN(n)));
  const rowForFilter = { ...rowData, _crm_states_for_filter: crmStates };
  const effectiveId = getEffectiveCrmTicketStatusId(rowForFilter);
  return effectiveId != null && selected.has(Number(effectiveId));
}

function TicketNumberFormatter(props) {
  return <TruncateSpan text={props.cell.getValue()} />;
}

function ThemeFormatter(props) {
  return <TruncateSpan text={props.cell.getValue()} />;
}

/** Bodka stavu akvizície/dopytu pred názvom partnera (parita Stroj + MachineStateDot). */
function WorkflowStateDot({ rowData }) {
  const color = rowData?.semaphore_b_color;
  const label = rowData?.semaphore_b_label ? String(rowData.semaphore_b_label) : '';
  if (!color) return null;
  return (
    <span
      className="inline-block h-4 w-4 shrink-0 rounded-full border border-gray-300 mr-2"
      style={{ backgroundColor: crmStateHexColor(color) }}
      title={label}
      aria-hidden
    />
  );
}

function NextStepFormatter(props) {
  const rowData = props.cell.getRow().getData();
  const label = resolveWorkflowNextStepListLabel(rowData, props.t);
  return <TruncateSpan text={label || '—'} />;
}

function PartnerNameFormatter(props) {
  const rowData = props.cell.getRow().getData();
  const name = rowData?.primary_company_name ? String(rowData.primary_company_name) : '—';
  const showAcqQteSemaphore = rowData?.type === 'acquisition' || rowData?.type === 'quote_request';
  return (
    <div className="flex items-center min-w-0 gap-2">
      {showAcqQteSemaphore ? <WorkflowStateDot rowData={rowData} /> : null}
      <span className="truncate" title={name}>
        {name}
      </span>
    </div>
  );
}

function CrmTypeFormatter({ cell, t }) {
  const rowData = cell.getRow().getData();
  const typeTitle = t(crmCommunicationTypeLabelKey(rowData.type));
  return (
    <div className="flex items-center justify-center w-full h-full" title={typeTitle}>
      {renderCrmCommunicationTypeIcon(rowData.type, { width: 6, height: 6, title: typeTitle })}
    </div>
  );
}

function FoundedCell(props) {
  const { formatDate, dateTimeFmt, getUserInitials, getUserColor } = props;
  const row = props.cell.getRow().getData();
  const name = row.created_by_name ? String(row.created_by_name) : '';
  const username = row.created_by_username ? String(row.created_by_username) : '';
  const text = formatDate ? formatDate(row.created_at, dateTimeFmt, true) : String(row.created_at || '');
  return (
    <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
      {name ? (
        <Avatar
          className="shrink-0"
          style={{ backgroundColor: getUserColor?.(username || name) || '#6b7280', color: '#ffffff' }}
          title={name}
          label={getUserInitials?.(name)}
          shape="circle"
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{text}</span>
    </div>
  );
}

function LastActivityCell(props) {
  const { formatDate, dateTimeFmt, getUserInitials, getUserColor } = props;
  const row = props.cell.getRow().getData();
  const name = row.last_activity_user_name ? String(row.last_activity_user_name) : '';
  const username = row.last_activity_username ? String(row.last_activity_username) : '';
  const text = formatDate ? formatDate(row.last_activity_at, dateTimeFmt, true) : String(row.last_activity_at || '');
  if (!name && !text) return null;
  return (
    <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
      {name ? (
        <Avatar
          className="shrink-0"
          style={{ backgroundColor: getUserColor?.(username || name) || '#6b7280', color: '#ffffff' }}
          title={name}
          label={getUserInitials?.(name)}
          shape="circle"
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{text}</span>
    </div>
  );
}

function OfferByAtCell(props) {
  const { formatDate, dateTimeFmt } = props;
  const row = props.cell.getRow().getData();
  const name = row.quote_offer_by_name ? String(row.quote_offer_by_name) : '';
  const date = row.quote_offer_submitted_at
    ? (formatDate ? formatDate(row.quote_offer_submitted_at, dateTimeFmt, true) : String(row.quote_offer_submitted_at))
    : '';
  return <TruncateSpan text={[name, date].filter(Boolean).join(' · ')} />;
}

function ExecutionDeadlineCell(props) {
  const {
    formatDate,
    dateFmt,
    getUserInitials,
    getUserColor,
    timezone = 'Europe/Bratislava',
    crmStates = [],
  } = props;
  const row = props.cell.getRow().getData();
  return (
    <CrmTicketListDeadlineCell
      rowData={row}
      crmStates={crmStates}
      formatDate={formatDate}
      dateFmt={dateFmt}
      getUserInitials={getUserInitials}
      getUserColor={getUserColor}
      timezone={timezone}
    />
  );
}

/** Parita FP defined_id sort – číselná časť za pomlčkou (ACQ-006). */
export function ticketCodeSorter(_a, _b, rowA, rowB) {
  const codeA = String(rowA.getData()?.ticket_code ?? '');
  const codeB = String(rowB.getData()?.ticket_code ?? '');
  const numA = codeA.includes('-') ? Number(codeA.split('-').pop()) : Number(codeA);
  const numB = codeB.includes('-') ? Number(codeB.split('-').pop()) : Number(codeB);
  const validA = !Number.isNaN(numA) && codeA !== '';
  const validB = !Number.isNaN(numB) && codeB !== '';
  if (validA && validB) {
    if (numA < numB) return -1;
    if (numA > numB) return 1;
    return codeA.localeCompare(codeB, 'sk');
  }
  if (validA && !validB) return -1;
  if (!validA && validB) return 1;
  return codeA.localeCompare(codeB, 'sk');
}

/** Parita ServiceTaskList::awaitingUserSorter – sort podľa deadline, prázdne na konci. */
export function executionDeadlineSorter(_a, _b, rowA, rowB) {
  const dataA = rowA.getData();
  const dataB = rowB.getData();
  const deadlineA = dataA?.deadline ?? dataA?.waiting_for_action_deadline_at;
  const deadlineB = dataB?.deadline ?? dataB?.waiting_for_action_deadline_at;
  const dtA = deadlineA ? DateTime.fromISO(String(deadlineA)) : null;
  const dtB = deadlineB ? DateTime.fromISO(String(deadlineB)) : null;
  if (dtA?.isValid && dtB?.isValid) {
    if (dtA < dtB) return -1;
    if (dtA > dtB) return 1;
    return 0;
  }
  if (dtA?.isValid && !dtB?.isValid) return -1;
  if (!dtA?.isValid && dtB?.isValid) return 1;
  return 0;
}

/**
 * @param {object} opts
 * @param {function} opts.t
 * @param {boolean} [opts.portalView]
 * @param {CrmCommColumnLayoutMode} [opts.layoutMode]
 * @param {function(number, boolean): void} [opts.onToggleTicketRead] (ticketId, markAsUnread)
 * @param {function} [opts.onOpenDetail]
 * @param {function} [opts.onDelete]
 * @param {function} [opts.formatDate]
 * @param {string} opts.dateFmt
 * @param {string} opts.dateTimeFmt
 * @param {function} [opts.getUserInitials]
 * @param {function} [opts.getUserColor]
 * @param {function} [opts.textHeaderFilter]
 * @param {function} [opts.dateColumnFilter]
 * @param {Array<{ label: string, value: string }>} [opts.partnerCompanyOptions]
 * @param {Array} [opts.moduleSettings]
 * @param {object[]} [opts.crmStates]
 * @param {object[]} [opts.crmSteps]
 * @param {string} [opts.locale]
 * @param {string} [opts.timezone]
 */
export function buildCrmCommunicationTableColumns(opts) {
  const {
    t,
    portalView = false,
    layoutMode: layoutModeProp,
    stateListLayout = false,
    onToggleTicketRead,
    onOpenDetail,
    onDelete,
    formatDate,
    dateFmt,
    dateTimeFmt,
    getUserInitials,
    getUserColor,
    textHeaderFilter,
    dateColumnFilter,
    partnerCompanyOptions = [],
    moduleSettings = [],
    crmStates = [],
    crmSteps = [],
    locale = 'sk',
    timezone = 'Europe/Bratislava',
  } = opts;
  const layoutMode = layoutModeProp ?? (portalView ? 'portal' : 'crm_full');
  const crmTicketsLayout = layoutMode === 'crm_tickets';
  const fpPartnerColumn = crmTicketsLayout || portalView;
  const fpStatusColumn = crmTicketsLayout || portalView;
  const fpTypeReadColumns = crmTicketsLayout || portalView;
  const workflowStatusFilterOptions = buildCrmWorkflowStatusFilterOptions(crmStates, locale);
  const mw = (labelKey, kind = 'textInput') => tabulatorColumnMinWidthFromTitle(t(labelKey), kind);
  const md = (labelKey) => tabulatorDateUserColumnMinWidth(t(labelKey));

  const typeFilterValues = {
    '': t('CrmCommunication.List.FilterAllTypes'),
    ...Object.fromEntries(
      CRM_COMMUNICATION_TYPE_FILTER_VALUES.map((type) => [type, t(crmCommunicationTypeLabelKey(type))]),
    ),
  };

  const readFilterValues = {
    '': t('Common.All'),
    true: t('CrmCommunication.List.Unread'),
    false: t('CrmCommunication.List.ReadOk'),
  };

  const crmTypeFilterOptions = CRM_COMMUNICATION_TYPE_FILTER_VALUES.map((type) => ({
    label: t(crmCommunicationTypeLabelKey(type)),
    value: type,
  })).sort((a, b) => a.label.localeCompare(b.label, locale || 'sk'));

  const crmStepFilterOptions = (Array.isArray(crmSteps) ? crmSteps : [])
    .filter((row) => row?.active !== false)
    .map((row) => ({
      label: displayCrmStepName(row?.name, locale),
      value: String(row.id),
    }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', locale || 'sk'));

  const nextStepFilterOptions = [
    { label: t('Workflow.NextStep.Approval'), value: 'approval' },
    { label: t('Workflow.NextStep.Execution'), value: 'execution' },
    { label: t('Workflow.NextStep.Inspection'), value: 'inspection' },
  ];

  const defs = {
    rowSelection: {
      title: '',
      field: 'rowSelection',
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      titleFormatterParams: { rowRange: 'active' },
      hozAlign: 'center',
      width: 44,
      minWidth: 44,
      headerSort: false,
      resizable: false,
      frozen: true,
      print: false,
      download: false,
      cellClick: (e, cell) => {
        cell.getRow().toggleSelect();
      },
    },
    editor: {
      title: '',
      field: 'editor',
      width: 44,
      minWidth: 44,
      hozAlign: 'center',
      headerHozAlign: 'center',
      headerSort: false,
      resizable: false,
      frozen: true,
      print: false,
      download: false,
      formatter: onOpenDetail
        ? reactFormatter(<EditRowCell onOpenDetail={onOpenDetail} />)
        : undefined,
    },
    id: {
      title: t('ServiceTaskTable.Col.Id'),
      field: 'id',
      minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
      width: TABULATOR_LIST_ID_MIN_WIDTH,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    status_indicator: {
      title: '',
      field: 'status_indicator',
      width: 44,
      minWidth: 44,
      hozAlign: 'center',
      headerHozAlign: 'center',
      headerSort: false,
      resizable: false,
      frozen: true,
      headerFilter: 'list',
      headerFilterParams: { values: { '': t('Common.All'), red: '🔴', orange: '🟠', green: '🟢' }, clearable: true },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue) return true;
        return getStatusIndicatorFilterValue(
          mapCrmRowForStatusIndicator(rowData),
          moduleSettings,
          CRM_STATUS_INDICATOR_OPTS,
        ) === headerValue;
      },
      formatter: reactFormatter(
        <CrmStatusIndicatorCell moduleSettings={moduleSettings} t={t} locale={locale} />,
      ),
    },
    type: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.Type') : t('CrmCommunication.List.Type'),
      field: 'type',
      minWidth: fpTypeReadColumns ? 100 : mw('CrmCommunication.List.Type', 'multiselect'),
      width: fpTypeReadColumns ? 100 : undefined,
      widthGrow: fpTypeReadColumns ? 0 : 0.8,
      hozAlign: fpTypeReadColumns ? 'center' : 'left',
      headerHozAlign: fpTypeReadColumns ? 'center' : undefined,
      sorter: 'string',
      headerFilter: fpTypeReadColumns ? primeMultiSelectHeaderFilter : 'list',
      headerFilterParams: fpTypeReadColumns
        ? {
          placeholder: t('Common.All'),
          valueType: 'string',
          options: crmTypeFilterOptions,
        }
        : { values: typeFilterValues },
      headerFilterFunc: fpTypeReadColumns
        ? (headerValue, rowValue) => {
          if (!headerValue) return true;
          const selected = new Set(parseCrmHeaderMulti(headerValue));
          return selected.has(String(rowValue));
        }
        : undefined,
      formatter: fpTypeReadColumns
        ? reactFormatter(<CrmTypeFormatter t={t} />)
        : (cell) => t(crmCommunicationTypeLabelKey(cell.getValue())),
    },
    is_unread: {
      title: fpTypeReadColumns
        ? '<span style="font-size:20px;line-height:1;">✉</span>'
        : t('CrmCommunication.List.Read'),
      field: 'is_unread',
      minWidth: fpTypeReadColumns ? 92 : mw('CrmCommunication.List.Read', 'multiselect'),
      width: fpTypeReadColumns ? 92 : undefined,
      widthGrow: fpTypeReadColumns ? 0 : 0.6,
      hozAlign: 'center',
      headerHozAlign: 'center',
      headerSort: false,
      formatter: fpTypeReadColumns
        ? reactFormatter(<MailIconCell t={t} onToggleRead={onToggleTicketRead} />)
        : reactFormatter(<ReadCell t={t} />),
      headerFilter: fpTypeReadColumns ? primeMultiSelectHeaderFilter : 'list',
      headerFilterParams: fpTypeReadColumns
        ? {
          placeholder: t('Common.All'),
          valueType: 'string',
          options: [
            { label: t('Common.Unread'), value: 'true' },
            { label: t('Common.Read'), value: 'false' },
          ],
        }
        : { values: readFilterValues },
      headerFilterFunc: fpTypeReadColumns
        ? (headerValue, rowValue) => {
          if (!headerValue) return true;
          const selected = new Set(parseCrmHeaderMulti(headerValue).map((x) => String(x).toLowerCase()));
          const rowKey = (rowValue === true || rowValue === 'true' || rowValue === 1 || rowValue === '1') ? 'true' : 'false';
          return selected.has(rowKey);
        }
        : undefined,
    },
    ticket_code: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.TicketNumber') : t('CrmCommunication.List.Code'),
      field: 'ticket_code',
      minWidth: crmTicketsLayout ? mw('ServiceTaskTable.Col.TicketNumber') : mw('CrmCommunication.List.Code'),
      widthGrow: crmTicketsLayout ? 1 : 0.9,
      hozAlign: 'left',
      sorter: crmTicketsLayout ? ticketCodeSorter : 'string',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      formatter: crmTicketsLayout ? reactFormatter(<TicketNumberFormatter />) : undefined,
    },
    title: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.Name') : t('CrmCommunication.List.TitleCol'),
      field: 'title',
      minWidth: crmTicketsLayout ? mw('ServiceTaskTable.Col.Name') : mw('CrmCommunication.List.TitleCol'),
      widthGrow: 2,
      hozAlign: 'left',
      sorter: 'string',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      formatter: crmTicketsLayout ? reactFormatter(<ThemeFormatter />) : undefined,
    },
    primary_company_name: {
      title: fpPartnerColumn
        ? (portalView ? t('ServiceTaskTable.Col.MachinePartnerName') : t('CrmCommunication.List.PartnerName'))
        : t('CrmCommunication.List.Customer'),
      field: 'primary_company_name',
      minWidth: fpPartnerColumn
        ? mw(portalView ? 'ServiceTaskTable.Col.MachinePartnerName' : 'CrmCommunication.List.PartnerName', 'multiselect')
        : mw('CrmCommunication.List.Customer'),
      widthGrow: fpPartnerColumn ? 1.5 : 1,
      hozAlign: 'left',
      sorter: 'string',
      headerFilter: fpPartnerColumn && partnerCompanyOptions.length > 0
        ? primeMultiSelectHeaderFilter
        : inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: fpPartnerColumn && partnerCompanyOptions.length > 0
        ? {
            placeholder: t('Common.All'),
            valueType: 'string',
            options: partnerCompanyOptions,
          }
        : undefined,
      headerFilterFunc: fpPartnerColumn && partnerCompanyOptions.length > 0
        ? (headerValue, rowValue) => {
            if (!headerValue) return true;
            const names = new Set(parseCrmHeaderMulti(headerValue));
            const rowName = String(rowValue ?? '').trim();
            return rowName ? names.has(rowName) : false;
          }
        : textHeaderFilter,
      formatter: fpPartnerColumn ? reactFormatter(<PartnerNameFormatter />) : undefined,
    },
    // CUST030 Obchodník — BE trader_name (companies.contractor_id); remote filter/sort.
    trader_name: {
      title: t('CrmCommunication.List.Trader'),
      field: 'trader_name',
      minWidth: mw('CrmCommunication.List.Trader'),
      widthGrow: 1,
      hozAlign: 'left',
      sorter: 'string',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      formatter: (cell) => {
        const v = cell?.getValue?.();
        if (v == null) return '';
        const s = String(v).trim();
        return s;
      },
    },
    execution_crm_step_id: {
      title: t('CrmCommunication.List.NextStep'),
      field: 'workflow_next_step_label',
      minWidth: mw('CrmCommunication.List.NextStep', 'multiselect'),
      widthGrow: fpPartnerColumn ? 1.2 : 1,
      hozAlign: 'left',
      sorter: 'string',
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: {
        placeholder: t('Common.All'),
        valueType: 'string',
        options: nextStepFilterOptions,
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue) return true;
        const selected = new Set(parseCrmHeaderMulti(headerValue));
        const phase = String(rowData?.workflow_next_step_phase ?? '').trim();
        if (phase) return selected.has(phase);
        const label = resolveWorkflowNextStepListLabel(rowData, t);
        return nextStepFilterOptions.some((opt) => selected.has(opt.value) && opt.label === label);
      },
      formatter: reactFormatter(<NextStepFormatter t={t} />),
    },
    primary_contact_name: {
      title: t('CrmCommunication.List.Buyer'),
      field: 'primary_contact_name',
      minWidth: mw('CrmCommunication.List.Buyer'),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    created_at: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.Created') : t('CrmCommunication.List.FoundedBy'),
      field: 'created_at',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      minWidth: crmTicketsLayout ? md('ServiceTaskTable.Col.Created') : md('CrmCommunication.List.FoundedBy'),
      widthGrow: 1.2,
      hozAlign: 'left',
      formatter: reactFormatter(<FoundedCell formatDate={formatDate} dateTimeFmt={dateTimeFmt} getUserInitials={getUserInitials} getUserColor={getUserColor} />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateColumnFilter,
      headerFilterFuncParams: { name: 'created_at' },
    },
    status_id: {
      title: fpStatusColumn ? t('ServiceTaskTable.Col.Status') : t('CrmCommunication.List.InquiryState'),
      field: 'status_id',
      minWidth: fpStatusColumn
        ? Math.max(mw('ServiceTaskTable.Col.Status', 'multiselect'), 140)
        : mw('CrmCommunication.List.InquiryState'),
      widthGrow: fpStatusColumn ? 1.1 : 0.9,
      hozAlign: 'left',
      headerHozAlign: 'left',
      sorter: 'string',
      headerFilter: fpStatusColumn ? primeMultiSelectHeaderFilter : inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: fpStatusColumn
        ? {
            placeholder: t('Common.All'),
            valueType: 'number',
            options: workflowStatusFilterOptions,
          }
        : undefined,
      headerFilterFunc: fpStatusColumn
        ? (headerValue, rowValue, rowData) => crmTicketStatusHeaderFilterFunc(headerValue, rowValue, rowData, crmStates)
        : textHeaderFilter,
      formatter: fpStatusColumn
        ? reactFormatter(<CrmTicketStatusFormatter crmStates={crmStates} locale={locale} t={t} />)
        : reactFormatter(<CrmTicketStatusFormatter crmStates={crmStates} locale={locale} t={t} />),
    },
    waiting_for_action_by: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.Deadline') : t('CrmCommunication.List.WaitingForActionBy'),
      field: 'waiting_for_action_by',
      minWidth: crmTicketsLayout ? md('ServiceTaskTable.Col.Deadline') : md('CrmCommunication.List.WaitingForActionBy'),
      widthGrow: 1.3,
      hozAlign: 'left',
      formatter: reactFormatter(
        <ExecutionDeadlineCell
          formatDate={formatDate}
          dateFmt={dateFmt}
          getUserInitials={getUserInitials}
          getUserColor={getUserColor}
          timezone={timezone}
          crmStates={crmStates}
        />,
      ),
      sorter: executionDeadlineSorter,
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateColumnFilter,
      headerFilterFuncParams: { name: 'waiting_for_action_by' },
    },
    pricing_speed_days: {
      title: t('CrmCommunication.List.PricingSpeed'),
      field: 'pricing_speed_days',
      minWidth: mw('CrmCommunication.List.PricingSpeed'),
      widthGrow: 0.5,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    quote_offer_by_name: {
      title: t('CrmCommunication.List.OfferByAt'),
      field: 'quote_offer_by_name',
      minWidth: mw('CrmCommunication.List.OfferByAt'),
      widthGrow: 1.2,
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      formatter: crmTicketsLayout ? reactFormatter(<OfferByAtCell formatDate={formatDate} dateTimeFmt={dateTimeFmt} />) : undefined,
    },
    quote_price: {
      title: t('CrmCommunication.List.QuotePrice'),
      field: 'quote_price',
      minWidth: mw('CrmCommunication.List.QuotePrice'),
      widthGrow: 0.5,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    quote_offer_status: {
      title: t('CrmCommunication.List.OfferStatus'),
      field: 'quote_offer_status',
      minWidth: mw('CrmCommunication.List.OfferStatus'),
      widthGrow: 0.7,
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    decision_duration_days: {
      title: t('CrmCommunication.List.DecisionDuration'),
      field: 'decision_duration_days',
      minWidth: mw('CrmCommunication.List.DecisionDuration'),
      widthGrow: 0.5,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    last_activity_at: {
      title: crmTicketsLayout ? t('ServiceTaskTable.Col.Updated') : t('CrmCommunication.List.LastActivity'),
      field: 'last_activity_at',
      minWidth: crmTicketsLayout ? md('ServiceTaskTable.Col.Updated') : md('CrmCommunication.List.LastActivity'),
      widthGrow: crmTicketsLayout ? 1.2 : 1.1,
      hozAlign: 'left',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      formatter: reactFormatter(<LastActivityCell formatDate={formatDate} dateTimeFmt={dateTimeFmt} getUserInitials={getUserInitials} getUserColor={getUserColor} />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateColumnFilter,
      headerFilterFuncParams: { name: 'last_activity_at' },
    },
    rejection_reason: {
      title: t('CrmCommunication.List.Rejection'),
      field: 'rejection_reason',
      minWidth: mw('CrmCommunication.List.Rejection'),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    custom_delete: {
      title: t('ServiceTaskTable.Col.Delete'),
      field: 'custom_delete',
      hozAlign: 'center',
      width: 110,
      minWidth: 110,
      headerSort: false,
      resizable: false,
      print: false,
      download: false,
      formatter: reactFormatter(<DeleteRowCell onDelete={onDelete} />),
    },
  };

  const order = stateListLayout
    ? CRM_COMM_STATE_LIST_COLUMN_ORDER
    : (layoutMode === 'crm_tickets'
      ? CRM_COMM_TICKETS_COLUMN_ORDER
      : (layoutMode === 'portal' ? CRM_COMM_PORTAL_COLUMN_ORDER : CRM_COMM_COLUMN_ORDER));
  return order
    .map((field) => {
      if (field === 'custom_delete' && (!onDelete || stateListLayout)) return null;
      return defs[field];
    })
    .filter(Boolean);
}
